import { TutorialDefinition } from '../TutorialEngine.js';
import { CrossingRenderOptions } from './CrossingTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type CrossingScene = {
    gameState: string;
    renderOptions: CrossingRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1E2,2A3,3D4
// Full solution: F2F3,D2A2,D1E1,C3C4

const scenes: CrossingScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'F2F3,D2A2,D1E1,C3C4+D4',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Crossing, guide both\nballs through all digits', cell: 'C0', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: The two balls
    {
        gameState: '+E2',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'One ball falls down\noOne shifts right', cell: 'B0', anchor: 'center' },
            { type: 'text-label', text: 'Both wrap around\nthe grid', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Click a digit to\nsee their trajectory', cell: 'B4', anchor: 'near-below' },
            { type: 'pulse', cell: 'E2', color: COLORS.teal },
        ],
    },

    // Scene 3: Placing teleporters
    {
        gameState: 'E3C3,C2A2',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click to place a\nteleporter gate', cell: 'B0', anchor: 'center' },
            { type: 'text-label', text: 'Click in the same row\nor column to pair', cell: 'D5', anchor: 'center' },
            { type: 'pulse', cell: 'C2', color: COLORS.teal },
            { type: 'pulse', cell: 'A2', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                'E3C3',
                'E3C3+C2',
                'E3C3,C2A2',
            ],
        },
    },

    // Scene 4: Placement rules
    {
        gameState: 'E3C3',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Can\'t place on digits\nor between a pair', cell: 'B1', anchor: 'center' },
            { type: 'exclusion', cells: ['E2', 'A3', 'D4', 'D3'] },
            { type: 'pulse', cell: 'D3', color: COLORS.red },
        ],
    },

    // Scene 5: Pairs can't cross digits or other gates
    {
        gameState: 'E3C3+C2',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Pairs can\'t cross digits\nor other gates', cell: 'E1', anchor: 'center' },
            { type: 'exclusion', cells: ['C4', 'C5', 'E2', 'F2'] },
        ],
    },

    // Scene 6: Badges
    {
        gameState: 'F2F3,C3C4+D4',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Badges show the next\ndigit each ball hits', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Teal = correct', cell: 'B5', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Amber = wrong', cell: 'B5', anchor: 'near-below', color: COLORS.amber },
        ],
    },

];

/**
 * Crossing tutorial: 6 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/crossing.md for detailed scene descriptions.
 */
export const CROSSING_TUTORIAL: TutorialDefinition = {
    gameType: 'crossing',
    gridCode: '6/1-3:1E2,2A3,3D4',
    scenes,
};
